import React from 'react';
import PropTypes from 'prop-types';

export const ADD_RECIPIENT_STEPS = [
  { id: 1, short: '01', label: 'Currency' },
  { id: 2, short: '02', label: 'Type' },
  { id: 3, short: '03', label: 'Details' },
];

const WIZARD_CARD_CLASS =
  'overflow-hidden rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] p-8 shadow-[var(--shadow-md)]';

function AddRecipientStepper({ currentStep }) {
  return (
    <div className='flex flex-col gap-2.5'>
      <div className='flex gap-1.5'>
        {ADD_RECIPIENT_STEPS.map((step) => (
          <div
            key={step.id}
            className={`h-[3px] flex-1 rounded-[2px] transition-colors duration-200 ${
              step.id <= currentStep ? 'bg-[var(--n900)]' : 'bg-[var(--n400)]'
            }`}
          />
        ))}
      </div>
      <div className='flex gap-1.5'>
        {ADD_RECIPIENT_STEPS.map((step) => (
          <div
            key={step.id}
            className={`flex-1 text-[10.5px] font-semibold uppercase tracking-[0.12em] ${
              step.id === currentStep ? 'text-[var(--n900)]' : 'text-[var(--n400)]'
            }`}
          >
            {step.short} · {step.label}
          </div>
        ))}
      </div>
    </div>
  );
}

AddRecipientStepper.propTypes = {
  currentStep: PropTypes.number.isRequired,
};

const footerButtonClassName =
  'rounded-lg border border-[var(--color-border)] bg-transparent px-[22px] py-[13px] text-sm font-medium text-[var(--n800)] transition-colors hover:bg-[var(--off)] font-[family-name:var(--font-ui)]';

export function WizardFormFooter({
  onCancel,
  onBack,
  continueLabel = 'Continue',
  continueDisabled = false,
  onContinue,
  submit = false,
  showContinue = true,
}) {
  const continueClassName = continueDisabled
    ? 'inline-flex cursor-not-allowed items-center gap-2 rounded-lg border border-[#e5e7eb] bg-transparent px-6 py-[13px] text-sm font-medium text-[#7a99b5] font-[family-name:var(--font-ui)]'
    : 'inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--n800)] bg-transparent px-6 py-[13px] text-sm font-medium text-[var(--n800)] shadow-[var(--shadow-sm)] transition-[background,color,border-color,box-shadow] duration-150 hover:bg-[var(--off)] font-[family-name:var(--font-ui)]';

  return (
    <div className={`mt-7 flex items-center gap-4 ${showContinue ? 'justify-between' : 'justify-start'}`}>
      <div className='flex flex-wrap items-center gap-3'>
        {onBack && (
          <button type='button' onClick={onBack} className={`inline-flex items-center gap-2 ${footerButtonClassName}`}>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <path d='M19 12H5M12 19l-7-7 7-7' />
            </svg>
            Back
          </button>
        )}
        {onCancel && (
          <button type='button' onClick={onCancel} className={footerButtonClassName}>
            Cancel
          </button>
        )}
      </div>
      {showContinue && (
        <button
          type={submit ? 'submit' : 'button'}
          disabled={continueDisabled}
          onClick={submit ? undefined : onContinue}
          className={continueClassName}
        >
          {continueLabel}
          {!continueDisabled && (
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <path d='M5 12h14M12 5l7 7-7 7' />
            </svg>
          )}
        </button>
      )}
    </div>
  );
}

WizardFormFooter.propTypes = {
  onCancel: PropTypes.func,
  onBack: PropTypes.func,
  continueLabel: PropTypes.string,
  continueDisabled: PropTypes.bool,
  onContinue: PropTypes.func,
  submit: PropTypes.bool,
  showContinue: PropTypes.bool,
};

WizardFormFooter.defaultProps = {
  onCancel: undefined,
  onBack: undefined,
  continueLabel: 'Continue',
  continueDisabled: false,
  onContinue: undefined,
  submit: false,
  showContinue: true,
};

export default function AddRecipientWizardShell({
  currentStep,
  title,
  subtitle,
  children,
  footer,
  cardClassName = '',
}) {
  return (
    <div className='mx-auto flex w-full max-w-[720px] flex-col gap-6'>
      <div>
        <p className='mb-2 text-[10px] font-semibold uppercase tracking-[0.13em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
          Add recipient
        </p>
        <h1 className='m-0 font-display text-[38px] font-normal leading-[1.1] tracking-[-0.02em] text-[var(--n900)]'>{title}</h1>
        {subtitle ? (
          <p className='m-0 mt-2.5 max-w-[520px] text-sm leading-relaxed text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
            {subtitle}
          </p>
        ) : null}
      </div>

      <AddRecipientStepper currentStep={currentStep} />

      <div className={`${WIZARD_CARD_CLASS} ${cardClassName}`.trim()}>
        {children}
        {footer}
      </div>
    </div>
  );
}

AddRecipientWizardShell.propTypes = {
  currentStep: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  cardClassName: PropTypes.string,
};

AddRecipientWizardShell.defaultProps = {
  subtitle: null,
  footer: null,
  cardClassName: '',
};
